import React, { useEffect, useState } from "react";
import moment from "moment";
import * as Yup from "yup";
import { Box, Typography } from "@material-ui/core";
import { FieldArray, useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { useSelectedTheme, useTranslate } from "../../../../../../../../Hooks";
import { installmentSectionTableHeaderData } from "./installmentSectionTableHeaderData";
import { GetLookupItemsByLookupTypeName } from "../../../../../../../../Services";
import { PropertyPaymentPlan } from "../../../../../../../../Enums";
import { frequencyTypes } from "../../../../../../PaymentPlans/constants";
import { config } from "../../../../../../../../config";
import {
  BasicTable,
  CustomDatePickerComponent,
  CustomSwitch,
  CustomTableHeader,
  ReskinningInputs,
  TableEditDeleteActions,
  CustomButton,
  CustomCheckbox,
} from "../../../../../../../../Components";

// icons
import { Calendar, PlusIcon } from "../../../../../../../../assets/icons";

// styles
import useStyles from "./styles";

const initialInstallment = (frequency) => ({
  installmentTypeId: config.InstallmentTypes.Installment || 1,
  amountPercentage: "",
  frequencyId: frequency?.lookupItemId,
  isBasedOnBookingDate: false,
  frequencyValue: null,
  frequencyName: frequency?.lookupItemName?.toLowerCase(),
  numberOfInstallment: "",
  id: crypto.randomUUID(),
});

const singleInstallmentSchema = Yup.object().shape({
  amountPercentage: Yup.number()
    .typeError("Amount Percentage is required")
    .required("Amount Percentage is Required")
    .min(1, "Min amount should be at least 1%")
    .max(99, "Max amount is 99%"),
  frequencyValue: Yup.mixed().required("This field is required"),
});

function InstallmentSection() {
  const styles = useStyles();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { translate } = useTranslate("NewPropertiesView");
  const { values, setFieldValue, touched, errors } = useFormikContext();
  const { isDarkMode } = useSelector((state) => state.theme);
  const [monthlyFrequency, setMonthlyFrequency] = useState(null);
  const [dateFrequency, setDateFrequency] = useState(null);
  const [completionFrequency, setCompletionFrequency] = useState(null);
  const [newInstallment, setNewInstallment] = useState(
    initialInstallment(monthlyFrequency)
  );
  const [activeItem, setActiveItem] = useState({});
  const [localErrors, setLocalErrors] = useState({});

  const handleNewChange = (field, value) => {
    setNewInstallment((prev) => ({ ...prev, [field]: value }));
    setLocalErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleAdd = async (push) => {
    try {
      await singleInstallmentSchema.validate(newInstallment, {
        abortEarly: false,
      });
      push({
        ...newInstallment,
        id: crypto.randomUUID(),
      });
      setNewInstallment(initialInstallment(monthlyFrequency));
      setLocalErrors({});
    } catch (err) {
      if (err.inner) {
        const errorsObj = err.inner.reduce((acc, curr) => {
          acc[curr.path] = curr.message;
          return acc;
        }, {});
        setLocalErrors(errorsObj);
      }
    }
  };

  useEffect(() => {
    const getFrequencies = async () => {
      try {
        const res = await GetLookupItemsByLookupTypeName({
          lookUpName: PropertyPaymentPlan.Frequency.lookupTypeName,
          pageIndex: 1,
          pageSize: 100,
        });
        const monthFrequency = res.result.find(
          (item) => item.lookupItemName.toLowerCase() === frequencyTypes.MONTH
        );
        const dateFrequency = res.result.find(
          (item) => item.lookupItemName.toLowerCase() === frequencyTypes.DATE
        );
        const completionFrequency = res.result.find(
          (item) =>
            item.lookupItemName.toLowerCase() ===
            frequencyTypes.CONSTRUCTION_PROGRESS
        );
        setMonthlyFrequency(monthFrequency);
        setDateFrequency(dateFrequency);
        setCompletionFrequency(completionFrequency);

        setNewInstallment((prev) => ({
          ...prev,
          frequencyId: monthFrequency?.lookupItemId,
          frequencyName: monthFrequency?.lookupItemName?.toLowerCase(),
        }));
      } catch (error) {
        console.error("Error fetching frequencies:", error);
      }
    };
    getFrequencies();
  }, []);

  return (
    <Box className={styles.highlightedSections}>
      <FieldArray name="installment">
        {({ push, remove }) => (
          <>
            <Box className={styles.containerInputSection}>
              <Box style={{ display: "flex", gap: "16px 32px" }}>
                <Typography className={styles.titleFiled}>
                  {translate("installment")}
                </Typography>
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    maxWidth: "416px",
                  }}
                >
                  <Box style={{ display: "flex", gap: "8px", width: "344px" }}>
                    <CustomCheckbox
                      checked={newInstallment.isBasedOnBookingDate}
                      onChange={() => {
                        const isBasedOnBookingDate =
                          !newInstallment.isBasedOnBookingDate;
                        handleNewChange(
                          "isBasedOnBookingDate",
                          isBasedOnBookingDate
                        );
                        if (isBasedOnBookingDate) {
                          handleNewChange(
                            "frequencyName",
                            frequencyTypes.MONTH
                          );
                          handleNewChange(
                            "frequencyId",
                            monthlyFrequency?.lookupItemId
                          );
                        }
                      }}
                    />
                    <Typography>
                      {translate("installment-based-on-booking-date")}
                    </Typography>
                  </Box>
                  <ReskinningInputs
                    startAdornment={<span className={styles.adornment}>%</span>}
                    inputPlaceholder={translate(
                      "enter-installment-percentage (e.g., 25%)"
                    )}
                    style={{ maxWidth: "100%" }}
                    min={0}
                    max={99}
                    type="number"
                    idRef="activitiesSearchRef"
                    value={newInstallment.amountPercentage}
                    onInputChanged={(e) => {
                      handleNewChange("amountPercentage", e.target.value);
                    }}
                    error={!!localErrors.amountPercentage}
                    helperText={localErrors.amountPercentage}
                  />
                  {newInstallment.isBasedOnBookingDate && (
                    <Box
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        maxWidth: "416px",
                      }}
                    >
                      <ReskinningInputs
                        startAdornment={
                          <span className={styles.adornment}>
                            {translate("months")}
                          </span>
                        }
                        inputPlaceholder={translate("enter monthes e.g 12")}
                        style={{ maxWidth: "100%" }}
                        min={0}
                        type="number"
                        idRef="activitiesSearchRef"
                        value={newInstallment.frequencyValue}
                        onInputChanged={(e) => {
                          handleNewChange("frequencyValue", e.target.value);
                        }}
                        error={!!localErrors.frequencyValue}
                        helperText={localErrors.frequencyValue}
                      />
                      <Typography
                        style={{
                          color: palette.text.tertiary,
                          fontSize: "14px",
                        }}
                      >
                        {translate(
                          "enter-number-of-monthes-to-pay-the-selected-percentage"
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            {!newInstallment.isBasedOnBookingDate && (
              <Box className={styles.containerInputSection}>
                <Typography className={styles.titleFiled}>
                  {translate("trigger-type")}
                </Typography>
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    maxWidth: "416px",
                  }}
                >
                  <CustomSwitch
                    size={"md"}
                    label={translate("by-month")}
                    checked={
                      newInstallment.frequencyName === frequencyTypes.MONTH
                    }
                    onChange={() => {
                      handleNewChange("frequencyName", frequencyTypes.MONTH);
                      handleNewChange(
                        "frequencyId",
                        monthlyFrequency?.lookupItemId
                      );
                    }}
                    isDisabled={false}
                  />
                  <CustomSwitch
                    size={"md"}
                    label={translate("by-fixed-date")}
                    checked={
                      newInstallment.frequencyName === frequencyTypes.DATE
                    }
                    onChange={() => {
                      handleNewChange("frequencyName", frequencyTypes.DATE);
                      handleNewChange(
                        "frequencyId",
                        dateFrequency?.lookupItemId
                      );
                    }}
                    isDisabled={false}
                  />
                  <CustomSwitch
                    size={"md"}
                    label={translate("completion")}
                    checked={
                      newInstallment.frequencyName ===
                      frequencyTypes.CONSTRUCTION_PROGRESS
                    }
                    onChange={() => {
                      handleNewChange(
                        "frequencyName",
                        frequencyTypes.CONSTRUCTION_PROGRESS
                      );
                      handleNewChange(
                        "frequencyId",
                        completionFrequency?.lookupItemId
                      );
                    }}
                    isDisabled={false}
                  />
                </Box>
              </Box>
            )}
            {!newInstallment.isBasedOnBookingDate &&
              newInstallment.frequencyName === frequencyTypes.MONTH && (
                <>
                  <Box className={styles.containerInputSection}>
                    <Typography className={styles.titleFiled}>
                      {translate("number-of-installment")}
                    </Typography>
                    <ReskinningInputs
                      inputPlaceholder={translate("Enter (e.g., 10)")}
                      style={{ maxWidth: "416px" }}
                      min={0}
                      type="number"
                      idRef="activitiesSearchRef"
                      value={newInstallment.numberOfInstallment}
                      onInputChanged={(e) => {
                        handleNewChange("numberOfInstallment", e.target.value);
                      }}
                      error={!!localErrors.numberOfInstallment}
                      helperText={localErrors.numberOfInstallment}
                    />
                  </Box>
                  <Box className={styles.containerInputSection}>
                    <Typography className={styles.titleFiled}>
                      {translate("frequency")}
                    </Typography>
                    <Box
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        maxWidth: "416px",
                      }}
                    >
                      <ReskinningInputs
                        startAdornment={
                          <span className={styles.adornment}>
                            {translate("months")}
                          </span>
                        }
                        inputPlaceholder={translate("e.g., every 1, 2 months")}
                        style={{ maxWidth: "100%" }}
                        min={0}
                        type="number"
                        idRef="activitiesSearchRef"
                        value={newInstallment.frequencyValue}
                        onInputChanged={(e) =>
                          handleNewChange("frequencyValue", e.target.value)
                        }
                        error={!!localErrors.frequencyValue}
                        helperText={localErrors.frequencyValue}
                      />
                      <Typography
                        style={{
                          color: palette.text.tertiary,
                          fontSize: "14px",
                        }}
                      >
                        {translate(
                          "set-how-often-payments-are-made(e.g., 1 for monthly, 2 for every 2 months), and so on"
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            {!newInstallment.isBasedOnBookingDate &&
              newInstallment.frequencyName === frequencyTypes.DATE && (
                <Box className={styles.containerInputSection}>
                  <Typography className={styles.titleFiled}>
                    {translate("by-fixed-date")}
                  </Typography>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: "416px",
                    }}
                  >
                    <CustomDatePickerComponent
                      value={moment(newInstallment.frequencyValue)}
                      placeholder={"dd/mm/yyyy"}
                      customStyle={styles.datePicker}
                      withIcon
                      minDate={moment()}
                      translate={translate}
                      onDateChanged={(date) => {
                        handleNewChange(
                          "frequencyValue",
                          new Date(date).toLocaleDateString()
                        );
                      }}
                      startAdornment={
                        <Calendar
                          width="20"
                          height="20"
                          fill={palette.foreground.quarterary}
                        />
                      }
                      autoOk={translate("confirm")}
                      error={!!localErrors.frequencyValue}
                      helperText={localErrors.frequencyValue}
                    />
                    <Typography
                      style={{
                        color: palette.text.tertiary,
                        fontSize: "14px",
                      }}
                    >
                      {translate("select-installment-date")}
                    </Typography>
                  </Box>
                </Box>
              )}
            {!newInstallment.isBasedOnBookingDate &&
              newInstallment.frequencyName ===
                frequencyTypes.CONSTRUCTION_PROGRESS && (
                <Box className={styles.containerInputSection}>
                  <Typography className={styles.titleFiled}>
                    {translate("construction-progress")}
                  </Typography>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: "416px",
                    }}
                  >
                    <ReskinningInputs
                      startAdornment={
                        <span className={styles.adornment}>%</span>
                      }
                      inputPlaceholder={translate(
                        "enter-construction-percentage-(e.g., 25%)"
                      )}
                      style={{ maxWidth: "100%" }}
                      min={0}
                      max={99}
                      type="number"
                      idRef="activitiesSearchRef"
                      value={newInstallment.frequencyValue}
                      onInputChanged={(e) =>
                        handleNewChange("frequencyValue", e.target.value)
                      }
                      error={!!localErrors.frequencyValue}
                      helperText={localErrors.frequencyValue}
                    />
                    <Typography
                      style={{
                        color: palette.text.tertiary,
                        fontSize: "14px",
                      }}
                    >
                      {translate("enter-percentage-of-construction-progress")}
                    </Typography>
                  </Box>
                </Box>
              )}
            <Box className={styles.containerInputSection}>
              <Typography style={{ width: "35%" }}></Typography>
              <Box display="flex" justifyContent="flex-start" width="100%">
                <CustomButton
                  boxShadow="none"
                  variant="text"
                  color="secondary"
                  style={{ color: palette.button.tertiaryGray_fg }}
                  onClick={() => handleAdd(push)}
                >
                  <PlusIcon
                    width="20"
                    height="20"
                    fill={palette.button.tertiaryGray_fg}
                  />
                  {translate("add-another")}
                </CustomButton>
              </Box>
            </Box>
            <Box className={styles.containerInputSection}>
              <>
                <BasicTable
                  rowHeight={84}
                  setActiveItem={setActiveItem}
                  setActiveItemOnRowClick={(item) => {
                    setActiveItem(item);
                  }}
                  rowsData={values.installment || []}
                  columns={installmentSectionTableHeaderData(isDarkMode)?.map(
                    (column) => ({
                      ...column,
                      renderHeader: (params) => (
                        <CustomTableHeader
                          params={params}
                          title={column.headerName}
                          isHiddenFilter={true}
                        />
                      ),
                    })
                  )}
                  tableActions={({ anchorPosition }) => {
                    if (!activeItem || Object.keys(activeItem).length === 0)
                      return null;
                    const activeIndex = values.installment.findIndex(
                      (item) => item.id === activeItem.id
                    );

                    if (activeIndex === -1) return null;
                    return (
                      <div style={{ zIndex: "99999", backgroundColor: "red" }}>
                        <span>meelad</span>
                        <TableEditDeleteActions
                          // anchorPosition={anchorPosition}
                          // onDelete={() => {
                          //   remove(activeIndex);
                          //   setActiveItem({});
                          //   setAnchorPosition({ x: 0, y: 0 });
                          // }}
                          anchorPosition={{ y: 200 }}
                          onDelete={() => alert("Delete")}
                        />
                      </div>
                    );
                  }}
                  emptyState={{ NoRowsOverlay: () => <Box></Box> }}
                />
              </>
            </Box>
          </>
        )}
      </FieldArray>
    </Box>
  );
}

export default InstallmentSection;
